import { useState, type FormEvent } from "react";
import { Button } from "@/shared/components/Button";
import { BotaoVoltar } from "@/shared/components/BotaoVoltar";
import type { Ambiente, DadosAmbiente } from "@/modules/reservas/services/ambientesService";

const CLASSE_INPUT =
  "w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30";
const CLASSE_LABEL = "text-sm font-medium text-text";

interface FormularioAmbienteProps {
  valoresIniciais?: Ambiente | null;
  carregando: boolean;
  erro?: string | null;
  onSubmit: (dados: DadosAmbiente) => void;
}

export function FormularioAmbiente({
  valoresIniciais,
  carregando,
  erro,
  onSubmit,
}: FormularioAmbienteProps) {
  const [nome, setNome] = useState(valoresIniciais?.nome ?? "");
  const [descricao, setDescricao] = useState(valoresIniciais?.descricao ?? "");
  const [capacidade, setCapacidade] = useState(
    valoresIniciais?.capacidade != null ? String(valoresIniciais.capacidade) : "",
  );
  const [ativo, setAtivo] = useState(valoresIniciais?.ativo ?? true);

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      nome: nome.trim(),
      descricao: descricao.trim() || null,
      capacidade: capacidade ? Number(capacidade) : null,
      ativo,
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="ambiente-nome" className={CLASSE_LABEL}>
          Nome do ambiente
        </label>
        <input
          id="ambiente-nome"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Ex.: Templo, Sala 2, Salão Social"
          className={CLASSE_INPUT}
          required
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="ambiente-descricao" className={CLASSE_LABEL}>
          Descrição <span className="text-muted">(opcional)</span>
        </label>
        <textarea
          id="ambiente-descricao"
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          rows={2}
          placeholder="Para que serve este espaço."
          className={CLASSE_INPUT}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="ambiente-capacidade" className={CLASSE_LABEL}>
          Capacidade <span className="text-muted">(opcional)</span>
        </label>
        <input
          id="ambiente-capacidade"
          type="number"
          min={0}
          value={capacidade}
          onChange={(e) => setCapacidade(e.target.value)}
          placeholder="Nº de pessoas"
          className={CLASSE_INPUT}
        />
      </div>

      <label className="flex cursor-pointer items-center justify-between rounded-lg border border-border bg-surface-2 px-3 py-2.5">
        <span className={CLASSE_LABEL}>Ambiente ativo</span>
        <button
          type="button"
          role="switch"
          aria-checked={ativo}
          onClick={() => setAtivo((v) => !v)}
          className={`relative h-6 w-11 rounded-full transition-colors ${ativo ? "bg-primary" : "bg-border"}`}
        >
          <span
            className={`absolute top-0.5 h-5 w-5 rounded-full bg-surface transition-transform ${
              ativo ? "left-0.5 translate-x-5" : "left-0.5"
            }`}
          />
        </button>
      </label>

      {erro && (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          {erro}
        </p>
      )}

      <Button type="submit" carregando={carregando} className="mt-1">
        {valoresIniciais ? "Salvar alterações" : "Criar ambiente"}
      </Button>
      <BotaoVoltar disabled={carregando} className="-mt-2" />
    </form>
  );
}
